import { useMemo } from 'react'

import { useAppSelector } from 'hooks/reduxHooks'
import { useCuratedTripSummary } from 'tripPlanner/hooks/api'
import {
  FullTrip,
  TripSummary as TripSummaryType,
  TripSummaryItem,
} from 'tripPlanner/types/common'
import { groupCuratedTripSections } from 'tripPlanner/utils/groupCuratedTripSections'
import { getTripFilters } from 'tripPlanner/utils/selectors'
import {
  TripPlannerFilterTypeCount,
  filterTripSummary,
  getNonEmptyProductFilters,
} from 'tripPlanner/utils/summary'

export default function useCuratedTripSummaryData({
  trip,
}: {
  trip: FullTrip
}) {
  const filters = useAppSelector(getTripFilters)
  const {
    data: tripSummary,
    isLoading,
    isFetched,
  } = useCuratedTripSummary({
    tripId: trip.id,
  })

  const summarySections = useMemo(() => {
    const items = (tripSummary?.entries ?? [])
      .filter((e): e is TripSummaryItem => e.type === 'ITEM')
      .map((e) => e.item)
    return groupCuratedTripSections(trip, items)
  }, [trip, tripSummary?.entries])

  const availableSubFilters: Array<TripPlannerFilterTypeCount> = useMemo(() => {
    if (tripSummary === undefined) return []

    return getNonEmptyProductFilters(
      tripSummary.entries
        .filter(
          (i): i is TripSummaryItem =>
            i.type === 'ITEM' && i.item.type !== 'NOTE',
        )
        .map((i) => i.item),
    )
  }, [tripSummary])

  const filteredTripSummary: TripSummaryType | undefined = useMemo(() => {
    if (tripSummary) {
      const entries = filterTripSummary(tripSummary.entries, filters)
      return {
        id: tripSummary.id,
        entries,
      }
    }
    return undefined
  }, [tripSummary, filters])

  const itemCount: number = useMemo(() => {
    if (tripSummary) {
      return filterTripSummary(tripSummary.entries, []).length
    }
    return 0
  }, [tripSummary])

  return {
    tripSummary,
    isLoading,
    isFetched,
    summarySections,
    itemCount,
    filteredTripSummary,
    availableSubFilters,
    filters,
  }
}
